import React, { useEffect } from 'react';
import { getPortalMode } from './api';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import {
  RouterProvider,
  RoutesSwitch,
  useLocation,
  useNavigate,
} from './router/Router';
import { ClientLayout } from './components/layout/ClientLayout';
import { AdminLayout } from './components/layout/AdminLayout';

// Client Portal Pages
import { LoginPage } from './pages/client/LoginPage';
import { ConnectionsPage } from './pages/client/ConnectionsPage';
import { ProjectsPage } from './pages/client/ProjectsPage';
import { ProjectDetailPage } from './pages/client/ProjectDetailPage';
import { BillingPage } from './pages/client/BillingPage';
import { SettingsPage } from './pages/client/SettingsPage';

// Admin Console Pages
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminCompaniesPage } from './pages/admin/AdminCompaniesPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminProjectsPage } from './pages/admin/AdminProjectsPage';
import { AdminAdminsPage } from './pages/admin/AdminAdminsPage';
import { AdminSystemPage } from './pages/admin/AdminSystemPage';

function PortalRouterSwitch() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { hasClientToken, hasAdminToken, user, impersonation } = useAuth();
  const portalMode = getPortalMode();

  const isClientAuthenticated = Boolean(hasClientToken || user);
  const isAdminAuthenticated = Boolean(hasAdminToken);
  const isAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');

  useEffect(() => {
    // Enforce VITE_PORTAL_MODE boundaries
    if (portalMode === 'admin' && !isAdminRoute && !impersonation.active) {
      navigate(isAdminAuthenticated ? '/admin/companies' : '/admin/login', { replace: true });
      return;
    }
    if (portalMode === 'client' && isAdminRoute) {
      navigate(isClientAuthenticated ? '/projects' : '/login', { replace: true });
      return;
    }

    // Enforce authentication guards
    if (isAdminRoute) {
      if (!isAdminAuthenticated && pathname !== '/admin/login') {
        navigate('/admin/login', { replace: true });
        return;
      }
      if (isAdminAuthenticated && (pathname === '/admin' || pathname === '/admin/login')) {
        navigate('/admin/companies', { replace: true });
        return;
      }
    } else {
      if (!isClientAuthenticated && pathname !== '/login') {
        navigate('/login', { replace: true });
        return;
      }
      if (isClientAuthenticated && pathname === '/') {
        if (portalMode === 'admin' && !impersonation.active) {
          navigate('/admin/companies', { replace: true });
        } else {
          navigate('/projects', { replace: true });
        }
        return;
      }
    }
  }, [
    pathname,
    portalMode,
    isAdminRoute,
    isClientAuthenticated,
    isAdminAuthenticated,
    impersonation.active,
    navigate,
  ]);

  // Admin Console Routes
  if (isAdminRoute) {
    if (!isAdminAuthenticated || pathname === '/admin/login') {
      return <AdminLoginPage />;
    }

    return (
      <AdminLayout>
        <RoutesSwitch
          routes={[
            { path: '/admin/companies', element: <AdminCompaniesPage /> },
            { path: '/admin/companies/:companyId', element: <AdminCompaniesPage /> },
            { path: '/admin/companies/:companyId/:tab', element: <AdminCompaniesPage /> },
            { path: '/admin/users', element: <AdminUsersPage /> },
            { path: '/admin/projects', element: <AdminProjectsPage /> },
            { path: '/admin/projects/:projectId', element: <AdminProjectsPage /> },
            { path: '/admin/projects/:projectId/pipes', element: <AdminProjectsPage /> },
            { path: '/admin/admins', element: <AdminAdminsPage /> },
            { path: '/admin/system', element: <AdminSystemPage /> },
            { path: '/admin/maintenance', element: <AdminSystemPage /> },
            { path: '/admin/settings', element: <AdminSystemPage /> },
          ]}
          fallback={<AdminCompaniesPage />}
        />
      </AdminLayout>
    );
  }

  // Client Login / Company Picker Routes (or unauthenticated fallback)
  if (!isClientAuthenticated || pathname === '/login' || pathname === '/auth/procore-company') {
    return <LoginPage />;
  }

  // Authenticated Client Portal Routes
  return (
    <ClientLayout>
      <RoutesSwitch
        routes={[
          { path: '/projects', element: <ProjectsPage /> },
          { path: '/projects/:projectId', element: <ProjectDetailPage /> },
          { path: '/connection', element: <ConnectionsPage /> },
          { path: '/connections', element: <ConnectionsPage /> },
          { path: '/billing', element: <BillingPage /> },
          { path: '/license', element: <BillingPage /> },
          { path: '/settings', element: <SettingsPage /> },
          { path: '/settings/user', element: <SettingsPage /> },
          { path: '/settings/company', element: <SettingsPage /> },
        ]}
        fallback={<ProjectsPage />}
      />
    </ClientLayout>
  );
}

export function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <RouterProvider>
          <PortalRouterSwitch />
        </RouterProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
